import {PublicSurveyFailure, PublicSurveyPayload} from "./publicSurvey.api";

/** Where the guest's Submit stands. 'failed' is the inline "could not be sent" line, kept until the next Submit. */
export type PublicSurveySubmit = 'idle' | 'sending' | 'failed';

/*
    The guest page's lifecycle, one phase at a time, so the combinations that cannot happen - sending and sent, a
    payload and a load failure - cannot be written down either. The flow inside the survey (answers, problems, the
    page, the welcome) is not part of it and keeps the preview's shape (runner/SurveyRunner.tsx).
*/
export type PublicSurveyPhase =
    | {kind: 'loading'}
    | {kind: 'unavailable', failure: PublicSurveyFailure}
    | {kind: 'answering', payload: PublicSurveyPayload, submit: PublicSurveySubmit}
    | {kind: 'alreadyAnswered', payload: PublicSurveyPayload}
    | {kind: 'linkGone', payload: PublicSurveyPayload}
    | {kind: 'submitted', payload: PublicSurveyPayload};

export type PublicSurveyPhaseAction =
    | {type: 'loadStarted'}
    | {type: 'loaded', payload: PublicSurveyPayload}
    | {type: 'loadFailed', failure: PublicSurveyFailure}
    | {type: 'submitStarted'}
    | {type: 'submitStopped', failure?: PublicSurveyFailure}
    | {type: 'submitSucceeded'};

export const LOADING_PHASE: PublicSurveyPhase = {kind: 'loading'};

// A reply about the link itself replaces the survey; any other failure is the line under it.
const afterStoppedSubmit = (
    payload: PublicSurveyPayload,
    failure: PublicSurveyFailure | undefined
): PublicSurveyPhase => {
    switch (failure?.kind) {
        case undefined:
            return {kind: 'answering', payload, submit: 'idle'};
        case 'ALREADY_SUBMITTED':
            return {kind: 'alreadyAnswered', payload};
        case 'GONE':
        case 'NOT_FOUND':
            return {kind: 'linkGone', payload};
        default:
            return {kind: 'answering', payload, submit: 'failed'};
    }
};

/*
    An action outside the phase it belongs to returns that phase itself, so React skips the render. The payload is
    handed on as the same object: the page's memos and its open-tracking effect are keyed on it.
*/
export function publicSurveyPhaseReducer(phase: PublicSurveyPhase, action: PublicSurveyPhaseAction): PublicSurveyPhase {
    switch (action.type) {
        case 'loadStarted':
            // StrictMode runs the load effect twice; the second start changes nothing.
            return phase.kind === 'loading' ? phase : LOADING_PHASE;

        case 'loaded':
            if (phase.kind !== 'loading') return phase;

            return action.payload.alreadySubmitted
                ? {kind: 'alreadyAnswered', payload: action.payload}
                : {kind: 'answering', payload: action.payload, submit: 'idle'};

        case 'loadFailed':
            return phase.kind === 'loading' ? {kind: 'unavailable', failure: action.failure} : phase;

        case 'submitStarted':
            return phase.kind === 'answering' && phase.submit !== 'sending' ? {...phase, submit: 'sending'} : phase;

        case 'submitStopped':
            return phase.kind === 'answering' && phase.submit === 'sending'
                ? afterStoppedSubmit(phase.payload, action.failure)
                : phase;

        case 'submitSucceeded':
            return phase.kind === 'answering' && phase.submit === 'sending'
                ? {kind: 'submitted', payload: phase.payload}
                : phase;
    }
}

/** The survey the phase holds: none while loading or after a failed load. */
export function payloadOf(phase: PublicSurveyPhase): PublicSurveyPayload | undefined {
    return 'payload' in phase ? phase.payload : undefined;
}
